import React from 'react';
import { connect } from 'react-redux';
import QueryString from 'query-string';
import ReactTable from 'react-table';
import i18next from 'i18next';

import { PageTitle } from '../PageTitle';
import { Message } from '../Message';
import { RowButton } from '../RowButton';
import { TagsService } from '../../services';
import { AccessHelper, BaseTable } from '../../helpers';

@connect(state => {
  return {
    me: state.user.me
  }
})
class Tags extends React.Component {

  constructor(props) {
    super(props);

    const passedState = (props.history && props.history.location) ? props.history.location.state : null;

    this.DEFAULT_SORTED = [{ id: 'name', desc: false }];

    this.state = {
      localErrorHandler: this.handleLocalError.bind(this),
      localErrorMessage: null,
      hasError: false,
      data: [],
      pageCount: null,
      forcePage: null,
      totalElements: null,
      loading: true,
      parsedOptions: QueryString.parse(this.props.location.search),
      currentHistoryCount: passedState && passedState.currentHistoryCount ? passedState.currentHistoryCount : 0,
      viewAllowed: AccessHelper.userHasAccess(this.props.me.availableRoutes, '/admin/tags/view/:tagId'),
      editAllowed: AccessHelper.userHasAccess(this.props.me.availableRoutes, '/admin/tags/edit/:tagId'),
      hasChecklistType: null
    };
  }

  componentDidMount() {
    TagsService.getAllTypes().then(response => {
      this.setState({
        hasChecklistType: response.types.find(type => type.token === 'checklist-passive-device') !== undefined
      });
    }).catch(err => {
      console.error(err);
      this.setState({
        hasChecklistType: false
      });
    });
  }

  messageDismissCallback() {
    this.setState({ localErrorMessage: null });
  }

  renderMessages() {
    if (this.state.localErrorMessage) {
      return (
        <Message
          errorMessage={this.state.localErrorMessage}
          messageDismissCallback={this.messageDismissCallback.bind(this)}
        />
      );
    }

    if (this.props.location.state) {
      return (
          <Message messages={this.props.location} />
      );
    }
    return null;
  }

  handleLocalError(localErrorMessage) {
    this.setState({ localErrorMessage: localErrorMessage });
  }

  fetchData(tableData) {
    if (this.updateRequestURL(tableData)) {
      return;
    }

    this.setState({
      loading: true
    });

    this.makeDatatableRequest(tableData);
  }

  makeDatatableRequest(tableData) {
    const options = BaseTable.buildFetchDataOptions(this.state.parsedOptions, this.DEFAULT_SORTED);

    TagsService.getAll(options).then(response => {
      const pageCount = Math.ceil(response.page.totalElements / this.refs.tagsTable.state.pageSize);

      let forcePage = null;
      if (this.refs.tagsTable && this.refs.tagsTable.state
        && this.refs.tagsTable.state.page >= pageCount) {
        forcePage = pageCount - 1;
      }
      this.setState({
        data: response.tags,
        pageCount: pageCount,
        forcePage: forcePage,
        totalElements: response.page.totalElements,
        loading: false
      });
    }).catch(error => {
      console.error(error);
      this.setState({ hasError: true });
      this.state.localErrorHandler(error.message);
    });
  }

  updateRequestURL(tableData) {
    const options = BaseTable.buildURLOptions(tableData, this.DEFAULT_SORTED);

    const params = options.map(option => Object.keys(option).map((k, i) => `${k}=${option[k]}`)).join('&');

    const currentSearch = this.props.location.search ? this.props.location.search : '?';

    if (`?${params}` !== currentSearch) {
      if (this.state.currentHistoryCount > 100) {
        console.error('Exceeded the history count stack of 100.');
        return false;
      }
      this.props.history.push({
        search: `?${params}`,
        state: {
          currentHistoryCount: this.state.currentHistoryCount + 1
        }
      });
      return true;
    } else {
      return false;
    }
  }

  render() {
    if (this.state.hasChecklistType === null) {
      return (<div className="loader">{i18next.t('loading')}</div>)
    } else {
      let dynamicOptions = BaseTable.buildDynamicOptions(this.state.parsedOptions, this.state.forcePage, this.DEFAULT_SORTED);
      return (
        <div className="op-content-wrapper">
          <div className="row">
            <PageTitle title={i18next.t('pages.tags.header')}
                        addLinkWithParams={{
                          link: {
                            pathname: "/admin/tags/add",
                            state: {
                              backLinkSearch: this.props.location.search
                            }
                          }
                        }} />
          </div>
          {this.renderMessages()}
          <div className="op-content-box">
            <div className="react-table-custom">
              <ReactTable
                className="-striped -highlight"
                ref="tagsTable"
                manual // Forces table not to paginate or sort automatically, so we can handle it server-side
                data={this.state.data}
                pages={this.state.pageCount} // Display the total number of pages
                loading={this.state.loading} // Display the loading overlay when we need it
                totalElements={this.state.totalElements}
                onFetchData={this.fetchData.bind(this)} // Request new data when things change
                showPaginationTop
                columns={[
                            {
                              Header: i18next.t('table-headers.name'),
                              accessor: "name",
                              sortable: true,
                              minResizeWidth: 50
                            },
                            {
                              Header: i18next.t('table-headers.type'),
                              accessor: "tagType.name",
                              Cell: cell => <span>{i18next.t(`tag-types.${cell.original.tagType.token}`)}</span>,
                              sortable: true,
                              minResizeWidth: 50
                            },
                            {
                              Header: i18next.t('table-headers.thresholds'),
                              accessor: "thresholdCount",
                              sortable: false,
                              minResizeWidth: 50
                            },
                            {
                              Header: i18next.t('table-headers.assets'),
                              Cell: ({original}) => <span>{original.deviceCount + original.assetCount}</span>,
                              sortable: false,
                              minResizeWidth: 50
                            },
                            {
                              Header: i18next.t('table-headers.checklists'),
                              accessor: "checklistCount",
                              sortable: false,
                              minResizeWidth: 50,
                              show: this.state.hasChecklistType
                            },
                            {
                              Cell: row => <RowButton
                                            allowView={this.state.viewAllowed}
                                            allowEdit={this.state.editAllowed}
                                            viewPath={{
                                              pathname: `/admin/tags/view/${row.original.id}`,
                                              state: { backLinkSearch: this.props.location.search }
                                            }}
                                            editPath={{
                                              pathname: `/admin/tags/edit/${row.original.id}`,
                                              state: { backLinkSearch: this.props.location.search }
                                            }} />,
                              sortable: false,
                              minResizeWidth: 50,
                              width: 150
                            }
                          ]}
                {...dynamicOptions}
              />
            </div>
          </div>
        </div>
      );
    }
  }
}

export default Tags;
